import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  amenitiesApi,
  type AmenityBlock,
  type AnonymousCheckoutOut,
  type AnonymousCheckoutStatusOut,
  type PublicVacuumOut
} from '@/api/amenities';
import { VacuumCountdown } from '@/components/amenities/VacuumCountdown';
import { useAuthStore } from '@/store/auth';
import { formatBRL } from '@/lib/format';
import { formatCPF, unmaskDigits } from '@/lib/masks';
import { friendlyMessage } from '@/lib/errors';

const STATUS_POLL_MS = 3_000;
const SUPPORT_URL = import.meta.env.VITE_SUPPORT_WHATSAPP_URL as string | undefined;

function minutesLabel(seconds: number): string {
  return `${Math.round(seconds / 60)}`;
}

/**
 * VacuumLanding — página PÚBLICA do QR físico do aspirador (`/v/:stationId`).
 *
 * Um QR só pra todo mundo (decisão Saulo 2026-07-08): dentro do app Bow o
 * scanner normaliza pro fluxo logado com saldo; na câmera do celular cai
 * aqui — escolhe o pacote, paga por Pix SEM CONTA e o relé liga via webhook.
 * Precedente de rota pública standalone: QRLanding (/qr/:stationId).
 */
export default function VacuumLanding() {
  const { stationId } = useParams();
  const token = useAuthStore((s) => s.token);

  const [info, setInfo] = useState<PublicVacuumOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeconds, setSelectedSeconds] = useState<number | null>(null);
  // PagBank exige customer na order Pix (nome+CPF; email é alias da Bow).
  const [customerName, setCustomerName] = useState('');
  const [customerCpf, setCustomerCpf] = useState('');
  const [checkout, setCheckout] = useState<AnonymousCheckoutOut | null>(null);
  const [status, setStatus] = useState<AnonymousCheckoutStatusOut | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [simulateError, setSimulateError] = useState<string | null>(null);
  const showDevTools = import.meta.env.VITE_SHOW_DEV_TOOLS === '1';

  useEffect(() => {
    if (!stationId || token) return;
    let cancelled = false;
    amenitiesApi
      .publicVacuum(stationId)
      .then((v) => {
        if (cancelled) return;
        setInfo(v);
        if (v.blocks.length > 0) setSelectedSeconds(v.blocks[0].seconds);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(friendlyMessage(err, 'Aspirador indisponível nesta estação.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [stationId, token]);

  // Polling do status do checkout (pagamento → ativação → countdown).
  const checkoutId = checkout?.id ?? null;
  useEffect(() => {
    if (!checkoutId) return;
    let cancelled = false;
    const poll = () => {
      amenitiesApi
        .anonymousCheckoutStatus(checkoutId)
        .then((s) => {
          if (!cancelled) setStatus(s);
        })
        .catch(() => {
          /* silencioso — tenta no próximo tick */
        });
    };
    poll();
    const id = window.setInterval(poll, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [checkoutId]);

  const onCopy = useCallback(async () => {
    if (!checkout) return;
    try {
      await navigator.clipboard.writeText(checkout.qr_code_text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [checkout]);

  // Logado → fluxo com saldo (mesma URL de destino que o scanner interno usa).
  if (token && stationId) {
    return <Navigate to={`/amenities/${stationId}/vacuum`} replace />;
  }

  const block: AmenityBlock | undefined =
    info?.blocks.find((b) => b.seconds === selectedSeconds) ?? info?.blocks[0];

  const cpfDigits = unmaskDigits(customerCpf);
  const customerOk = customerName.trim().length >= 3 && cpfDigits.length === 11;

  async function onPay() {
    if (!info || !block || !customerOk) return;
    setCreating(true);
    setError(null);
    try {
      const c = await amenitiesApi.createAnonymousCheckout(
        info.device_id,
        block.seconds,
        customerName.trim(),
        cpfDigits
      );
      setCheckout(c);
    } catch (err) {
      setError(friendlyMessage(err, 'Não foi possível gerar o Pix. Tente de novo.'));
    } finally {
      setCreating(false);
    }
  }

  const session = status?.session ?? null;
  const paidUnfulfilled = status?.status === 'paid' && status.fulfillment_error;
  const running = session !== null && !paidUnfulfilled;

  // ---- fase 3: sessão rodando (countdown imersivo navy) ----
  if (running && session) {
    return (
      <div className="relative flex h-[100dvh] flex-col items-center justify-center overflow-hidden bg-bow-royal px-6 text-bow-cream">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full"
          style={{ background: 'var(--grad-arc-cool)', opacity: 0.2, filter: 'blur(60px)' }}
        />
        <VacuumCountdown session={session} />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-bow-cream text-bow-royal">
      <div
        className="mx-auto flex max-w-md flex-col gap-4 px-5 pb-10"
        style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <img src="/logo/logo-bow-13.png" alt="Bow" className="h-10 w-10 object-contain" />
          <div>
            <div
              className="leading-tight"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: 20,
                letterSpacing: '-0.02em'
              }}
            >
              Aspirador Bow
            </div>
            <div className="text-[12.5px] text-bow-royal/60">
              {info?.station_name ?? 'Estação Bow'}
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl bg-white p-4 text-sm text-bow-red shadow-soft">
            {error}
          </div>
        )}

        {paidUnfulfilled && (
          <div className="flex flex-col gap-2 rounded-2xl bg-white p-5 shadow-soft">
            <span className="font-semibold text-bow-red">
              Pagamento recebido, mas o aspirador não ligou
            </span>
            <p className="text-sm text-bow-royal/70">
              Alguém pode ter começado a usar antes de você, ou o equipamento está
              fora do ar. Fale com a gente pra devolver o valor.
            </p>
            {SUPPORT_URL && (
              <a
                href={SUPPORT_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex h-11 items-center justify-center rounded-full bg-bow-royal px-5 text-sm font-semibold text-bow-cream"
              >
                Falar no WhatsApp
              </a>
            )}
          </div>
        )}

        {/* ---- fase 2: Pix gerado, aguardando pagamento ---- */}
        {checkout && !paidUnfulfilled && (
          <>
            <div className="flex flex-col items-center gap-3 rounded-3xl bg-white p-6 shadow-soft">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/50">
                Pix de
              </span>
              <span
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 600,
                  fontSize: 36,
                  letterSpacing: '-0.03em'
                }}
              >
                {formatBRL(checkout.price_brl)}
              </span>
              <div className="rounded-2xl border border-bow-royal/10 bg-white p-3">
                <QRCodeSVG
                  value={checkout.qr_code_text}
                  size={170}
                  bgColor="#FFFFFF"
                  fgColor="#001B74"
                  level="M"
                />
              </div>
              <span className="text-xs text-bow-royal/60">
                {minutesLabel(checkout.block_seconds)} min de aspiração · o QR acima é
                pra pagar de outro celular
              </span>
            </div>

            <div className="flex flex-col gap-2 rounded-3xl bg-white p-4 shadow-soft">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/50">
                Pix copia e cola
              </span>
              <code className="max-h-24 overflow-hidden break-all rounded-2xl bg-bow-royal/5 p-3 text-xs leading-relaxed">
                {checkout.qr_code_text}
              </code>
              <button
                type="button"
                onClick={onCopy}
                className="inline-flex h-12 items-center justify-center rounded-full bg-bow-royal text-sm font-semibold text-bow-cream"
              >
                {copied ? 'Copiado!' : 'Copiar código Pix'}
              </button>
              <p className="text-center text-xs text-bow-royal/55">
                Cole no app do seu banco. Assim que o pagamento cair, o aspirador liga
                sozinho — deixe esta página aberta.
              </p>
            </div>

            <div className="flex items-center gap-2 rounded-2xl bg-bow-royal/5 p-4">
              <span className="h-2 w-2 animate-pulse rounded-full bg-bow-blue" />
              <span className="text-sm text-bow-royal/75">
                Aguardando pagamento…
              </span>
            </div>

            {showDevTools && (
              <button
                type="button"
                onClick={() => {
                  setSimulateError(null);
                  amenitiesApi
                    .devSimulateAnonymousPayment(checkout.id)
                    .catch((err) =>
                      setSimulateError(friendlyMessage(err, 'simulate falhou'))
                    );
                }}
                className="text-xs text-bow-royal/40 underline"
              >
                [dev] simular pagamento
              </button>
            )}
            {simulateError && (
              <span className="text-[11px] text-bow-red">{simulateError}</span>
            )}
          </>
        )}

        {/* ---- fase 1: escolha do pacote ---- */}
        {!checkout && info && (
          <>
            {!info.is_available && (
              <div className="rounded-2xl bg-white p-4 text-sm text-bow-royal/70 shadow-soft">
                O aspirador está <strong>em uso agora</strong>. Aguarde alguns minutos
                e recarregue a página.
              </div>
            )}

            <div className="flex flex-col gap-2.5">
              <span
                className="mt-1"
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 17,
                  letterSpacing: '-0.015em'
                }}
              >
                Escolha um pacote
              </span>
              <div className="grid grid-cols-2 gap-2.5">
                {info.blocks.map((b) => {
                  const selected = b.seconds === block?.seconds;
                  return (
                    <button
                      key={b.seconds}
                      type="button"
                      onClick={() => setSelectedSeconds(b.seconds)}
                      className={`flex flex-col items-start rounded-2xl border-[1.5px] bg-white p-4 text-left transition ${
                        selected ? 'border-bow-royal bg-bow-royal/[0.04]' : 'border-bow-royal/10'
                      }`}
                    >
                      <span className="text-[26px] font-semibold leading-none tracking-tight">
                        {minutesLabel(b.seconds)}
                        <span className="ml-1 text-sm font-medium text-bow-royal/55">min</span>
                      </span>
                      <span className="mt-2 w-full border-t border-bow-royal/8 pt-2 text-lg font-semibold tracking-tight">
                        {formatBRL(b.price_brl)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Nome + CPF — o Pix precisa de um pagador identificado
                (exigência do gateway), mas sem criar conta. */}
            <div className="flex flex-col gap-2 rounded-3xl bg-white p-4 shadow-soft">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-royal/50">
                Dados do pagador (sem cadastro)
              </span>
              <input
                type="text"
                autoComplete="name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Nome completo"
                className="h-12 rounded-2xl border border-bow-royal/15 bg-white px-4 text-base focus:border-bow-royal focus:outline-none"
              />
              <input
                type="text"
                inputMode="numeric"
                value={customerCpf}
                onChange={(e) => setCustomerCpf(formatCPF(e.target.value))}
                placeholder="CPF"
                className="h-12 rounded-2xl border border-bow-royal/15 bg-white px-4 text-base focus:border-bow-royal focus:outline-none"
              />
            </div>

            <div className="flex items-start gap-2.5 rounded-2xl border border-bow-royal/10 bg-bow-royal/[0.03] px-4 py-3">
              <span aria-hidden className="text-base leading-none">🧹</span>
              <span className="text-[13px] leading-snug text-bow-royal/70">
                Pague por Pix, <strong className="font-semibold">sem criar conta</strong> —
                o aspirador liga sozinho quando o pagamento cair e desliga ao fim do tempo.
              </span>
            </div>

            <button
              type="button"
              onClick={onPay}
              disabled={creating || !block || !info.is_available || !customerOk}
              className="inline-flex h-[54px] items-center justify-center rounded-full bg-bow-royal text-base font-semibold text-bow-cream disabled:cursor-not-allowed disabled:opacity-50"
            >
              {creating
                ? 'Gerando Pix…'
                : block
                  ? `Pagar com Pix · ${formatBRL(block.price_brl)}`
                  : 'Indisponível'}
            </button>

            <Link
              to="/login"
              state={{ from: { pathname: `/amenities/${stationId}/vacuum` } }}
              className="text-center text-sm font-semibold text-bow-royal/60 underline-offset-2 hover:underline"
            >
              Já sou cliente Bow — pagar com meu saldo
            </Link>
          </>
        )}

        {!info && !error && !checkout && (
          <div className="h-48 animate-pulse rounded-3xl bg-white shadow-soft" />
        )}
      </div>
    </div>
  );
}
